import React, { useState, useEffect } from 'react';
import { HEADER_TEMPLATES, HEADER_CATEGORIES, validateHeaderNames, suggestHeaderNames } from '../utils/headerTemplates';
import './HeaderImportModal.css';

interface HeaderImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportHeaders: (headers: string[]) => void;
  currentHeaders?: string[];
}

const HeaderImportModal: React.FC<HeaderImportModalProps> = ({
  isOpen,
  onClose,
  onImportHeaders,
  currentHeaders = []
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [customHeaders, setCustomHeaders] = useState<string[]>(['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date']);
  const [newHeader, setNewHeader] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [headerInput, setHeaderInput] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [validation, setValidation] = useState<{ valid: boolean; errors: string[] }>({ valid: true, errors: [] });

  // Filter templates by category
  const filteredTemplates = selectedCategory === 'all' 
    ? HEADER_TEMPLATES 
    : HEADER_TEMPLATES.filter(t => t.category === selectedCategory);

  // Validate headers whenever they change
  useEffect(() => {
    const validation = validateHeaderNames(customHeaders);
    setValidation(validation);
  }, [customHeaders]);

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = HEADER_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setCustomHeaders([...template.headers]);
      setSelectedTemplate(templateId);
    }
  };

  // Add new header
  const handleAddHeader = () => {
    const trimmedHeader = headerInput.trim();
    if (trimmedHeader && !customHeaders.includes(trimmedHeader)) {
      setCustomHeaders([...customHeaders, trimmedHeader]);
      setHeaderInput('');
      setShowSuggestions(false);
    }
  };

  // Remove header
  const handleRemoveHeader = (index: number) => {
    const newHeaders = customHeaders.filter((_, i) => i !== index);
    setCustomHeaders(newHeaders);
  };

  // Handle header input with suggestions
  const handleHeaderInputChange = (value: string) => {
    setHeaderInput(value);
    if (value.trim()) {
      const suggestions = suggestHeaderNames(value);
      setSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Select suggestion
  const handleSelectSuggestion = (suggestion: string) => {
    setHeaderInput(suggestion);
    setShowSuggestions(false);
  };

  // Handle import
  const handleImport = () => {
    if (validation.valid && customHeaders.length > 0) {
      onImportHeaders(customHeaders);
      onClose();
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddHeader();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="header-import-modal-overlay" onClick={onClose}>
      <div className="header-import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="header-import-modal-header">
          <h2>📋 Import Header Templates</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="header-import-modal-content">
          {/* Category Filter */}
          <div className="category-filter">
            <label>Filter by Category:</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {Object.entries(HEADER_CATEGORIES).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>

          {/* Template Selection */}
          <div className="template-section">
            <h3>🎯 Choose a Template</h3>
            <div className="template-grid">
              {filteredTemplates.map((template) => (
                <div 
                  key={template.id}
                  className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <h4>{template.name}</h4>
                  <p>{template.description}</p>
                  <div className="template-headers">
                    {template.headers.slice(0, 4).map((header, index) => (
                      <span key={index} className="header-tag">{header}</span>
                    ))}
                    {template.headers.length > 4 && (
                      <span className="header-tag more">+{template.headers.length - 4} more</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Headers */}
          <div className="custom-headers-section">
            <h3>✏️ Custom Headers</h3>
            
            {/* Add New Header */}
            <div className="add-header-section">
              <div className="header-input-container">
                <input
                  type="text"
                  value={headerInput}
                  onChange={(e) => handleHeaderInputChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter header name..."
                  className="header-input"
                />
                <button 
                  onClick={handleAddHeader}
                  disabled={!headerInput.trim() || customHeaders.includes(headerInput.trim())}
                  className="add-header-button"
                >
                  Add
                </button>
              </div>
              
              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Current Headers */}
            <div className="current-headers">
              <label>Current Headers ({customHeaders.length}):</label>
              <div className="headers-list">
                {customHeaders.map((header, index) => (
                  <div key={index} className="header-item">
                    <span className="header-name">{header}</span>
                    <button 
                      onClick={() => handleRemoveHeader(index)}
                      className="remove-header-button"
                      title="Remove header"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Validation Errors */}
            {!validation.valid && (
              <div className="validation-errors">
                <h4>⚠️ Please fix the following errors:</h4>
                <ul>
                  {validation.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="preview-section">
            <h3>👀 Preview</h3>
            <div className="preview-table">
              <div className="preview-row">
                {customHeaders.map((header, index) => (
                  <div key={index} className="preview-cell header-cell">
                    {header}
                  </div>
                ))}
              </div>
              <div className="preview-row">
                {customHeaders.map((_, index) => (
                  <div key={index} className="preview-cell data-cell">
                    Sample Data {index + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="header-import-modal-footer">
          <button 
            onClick={onClose} 
            className="cancel-button"
          >
            Cancel
          </button>
          <button 
            onClick={handleImport}
            disabled={!validation.valid || customHeaders.length === 0}
            className="import-button"
          >
            📥 Import Headers
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderImportModal;














